/**
 * FeeConfigurationContainer.tsx
 * Main orchestrator component for fee configuration - handles data loading and coordination
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { Card, CardHeader, CardBody, Skeleton, Alert, Button } from '@enfomentis/ui';
import { FeeHierarchyTree } from './FeeHierarchyTree';
import { FeeModalManager } from './FeeModalManager';
import { useModalState, ModalType, type ModalContext } from '../utils/modalUtils';
import { useFeeHierarchy } from '../hooks/useFeeHierarchy';
import { useOnboardingData } from '../../../hooks/useOnboardingData';
import { BROKER_FEE_CATALOG_DATA } from '@/data/brokerFeeCatalog';
import type { BrokerFeeConfiguration } from '@/types/reference/BrokerFeeCatalog';
import type { InstrumentSelection } from '../modals';
import type { AssetClassType, MarketSegment, SubAssetClass } from '@/types/shared/AssetClass';
import { ASSET_CLASS_TYPE_DISPLAY_MAP, MARKET_SEGMENT_DISPLAY_MAP, SUB_ASSET_CLASS_DISPLAY_MAP } from '@/constants/assetClass';
import { formatAssetClassTypeDisplay, formatMarketSegmentDisplay, formatSubAssetClassDisplay } from '@/utils/assetClass';
import { transformFormToBrokerFeeConfig, type FeeFormData, type FeeAssignmentContext } from '../utils/feeFormTransforms';
import {
  FeeConfigurationLoadingState
} from '../utils/feeConfigurationUtils';

// Cache for loaded broker catalog fees to prevent re-processing
const brokerCatalogFeesCache = new Map<string, BrokerFeeConfiguration[]>();

/**
 * Load broker fee configurations from catalog and transform to BrokerFeeConfiguration format
 * Synchronous and cached for instant access
 */
const loadBrokerCatalogFees = (brokerId: string): BrokerFeeConfiguration[] => {
  // Check cache first
  const cachedFees = brokerCatalogFeesCache.get(brokerId);
  if (cachedFees) {
    return cachedFees;
  }

  try {
    // Lookup broker in catalog
    const catalogEntry = BROKER_FEE_CATALOG_DATA.get(brokerId);

    if (!catalogEntry || !catalogEntry.feeConfigurations) {
      console.warn('No fee configurations found for broker', brokerId);
      brokerCatalogFeesCache.set(brokerId, []);
      return [];
    }

    // Transform catalog fee configurations to our format
    const transformedFees: BrokerFeeConfiguration[] = catalogEntry.feeConfigurations.map(catalogConfig => {
      // Handle category mapping with proper optional property handling
      const category = catalogConfig.category ? {
        assetClass: catalogConfig.category.assetClass,
        marketSegment: catalogConfig.category.marketSegment,
        ...(catalogConfig.category.subAssetClass ? { subAssetClass: catalogConfig.category.subAssetClass } : {})
      } : undefined;

      // Handle overnight financing mapping
      const overnightFinancing = catalogConfig.overnightFinancing ? {
        adminSpread: catalogConfig.overnightFinancing.adminSpread,
        adminSpreadPrecision: catalogConfig.overnightFinancing.adminSpreadPrecision,
        ...(catalogConfig.overnightFinancing.benchmark ? { benchmark: catalogConfig.overnightFinancing.benchmark } : {}),
        ...(catalogConfig.overnightFinancing.frequency ? { frequency: catalogConfig.overnightFinancing.frequency } : {}),
        ...(catalogConfig.overnightFinancing.assetClass ? { assetClass: catalogConfig.overnightFinancing.assetClass } : {}),
        ...(catalogConfig.overnightFinancing.hasTripleCharges !== undefined ? { hasTripleCharges: catalogConfig.overnightFinancing.hasTripleCharges } : {}),
        ...(catalogConfig.overnightFinancing.tripleChargeDay ? { tripleChargeDay: catalogConfig.overnightFinancing.tripleChargeDay } : {}),
        ...(catalogConfig.overnightFinancing.verificationStatus ? { verificationStatus: catalogConfig.overnightFinancing.verificationStatus } : {}),
        ...(catalogConfig.overnightFinancing.verificationNotes ? { verificationNotes: catalogConfig.overnightFinancing.verificationNotes } : {})
      } : undefined;

      // Handle currency conversion mapping
      const currencyConversion = catalogConfig.currencyConversion ? {
        ...(catalogConfig.currencyConversion.rateType ? { rateType: catalogConfig.currencyConversion.rateType } : {}),
        ...(catalogConfig.currencyConversion.triggers ? { triggers: catalogConfig.currencyConversion.triggers } : {}),
        ...(catalogConfig.currencyConversion.markup !== undefined ? { markup: catalogConfig.currencyConversion.markup } : {}),
        ...(catalogConfig.currencyConversion.markupPrecision !== undefined ? { markupPrecision: catalogConfig.currencyConversion.markupPrecision } : {}),
        ...(catalogConfig.currencyConversion.usesRealTimeRates !== undefined ? { usesRealTimeRates: catalogConfig.currencyConversion.usesRealTimeRates } : {}),
        ...(catalogConfig.currencyConversion.verificationStatus ? { verificationStatus: catalogConfig.currencyConversion.verificationStatus } : {}),
        ...(catalogConfig.currencyConversion.verificationNotes ? { verificationNotes: catalogConfig.currencyConversion.verificationNotes } : {})
      } : undefined;

      return {
        id: catalogConfig.id,
        name: catalogConfig.name,
        ...(category ? { category } : {}),
        ...(catalogConfig.appliesTo ? { appliesTo: catalogConfig.appliesTo } : {}),
        commission: {
          feeType: catalogConfig.commission.feeType,
          feeAmount: catalogConfig.commission.feeAmount ?? 0,
          feePrecision: catalogConfig.commission.feePrecision ?? 2,
          chargeOn: catalogConfig.commission.chargeOn,
          verificationStatus: catalogConfig.commission.verificationStatus
        },
        ...(overnightFinancing ? { overnightFinancing } : {}),
        ...(currencyConversion ? { currencyConversion } : {})
      };
    });

    // Cache the result
    brokerCatalogFeesCache.set(brokerId, transformedFees);
    return transformedFees;

  } catch (error) {
    console.error('Error loading broker catalog fees:', error);
    const emptyResult: BrokerFeeConfiguration[] = [];
    brokerCatalogFeesCache.set(brokerId, emptyResult);
    return emptyResult;
  }
};

// Props for the container component
interface FeeConfigurationContainerProps {
  className?: string;
}

export const FeeConfigurationContainer: React.FC<FeeConfigurationContainerProps> = ({
  className = ""
}) => {
  // Get onboarding draft data and hierarchy operations
  const { draft, loading: draftLoading, updateFeeConfiguration, isFeeConfigurationComplete } = useOnboardingData();
  const [hierarchy, hierarchyOperations] = useFeeHierarchy();

  // Track initialization to prevent flash
  const hasInitializedRef = React.useRef(false);

  // Component loading states - initialize to READY if fees already exist
  const [loadingState, setLoadingState] = useState<FeeConfigurationLoadingState>(() => {
    // Check if we already have fee configurations on mount
    if (draft?.account?.feeConfiguration?.customFeeConfigs?.length) {
      hasInitializedRef.current = true;
      return FeeConfigurationLoadingState.READY;
    }
    return FeeConfigurationLoadingState.IDLE;
  });

  // Validation state
  const [hierarchyErrors, setHierarchyErrors] = useState<string[]>([]);

  // Modal state management
  const { context, activeModal, openModal, closeModal } = useModalState();

  // Real-time validation effect
  useEffect(() => {
    if (loadingState === FeeConfigurationLoadingState.READY) {
      // Simple validation for now - check if at least one fee is configured
      const errors: string[] = [];

      if (hierarchy.length === 0) {
        errors.push('No asset classes configured. Add at least one asset class or instrument.');
      } else if (!isFeeConfigurationComplete()) {
        errors.push('At least one fee must be configured across your hierarchy.');
      }

      // TODO: Implement more detailed validation if needed
      setHierarchyErrors(errors);
    }
  }, [hierarchy, loadingState, isFeeConfigurationComplete]);

  // Handle catalog broker fee pre-population and initialization
  useEffect(() => {
    if (draftLoading || !draft) {
      return; // Wait for draft to load
    }

    // Skip if already initialized
    if (hasInitializedRef.current) {
      return;
    }

    try {
      setLoadingState(FeeConfigurationLoadingState.INITIALIZING);

      // Check if this is a catalog broker that needs fee pre-population
      const selectedBrokerId = draft.account.selectedBrokerId;
      const brokerSnapshot = draft.account.brokerSnapshot;
      const isCustomBroker = draft.account.feeConfiguration?.isCustomBroker;

      // If user selected a catalog broker and hasn't configured fees yet
      if (selectedBrokerId && brokerSnapshot && !isCustomBroker && !draft.account.feeConfiguration?.customFeeConfigs?.length) {
        setLoadingState(FeeConfigurationLoadingState.LOADING_CATALOG);

        // Load broker fees from catalog (synchronous and cached)
        const brokerFees = loadBrokerCatalogFees(selectedBrokerId);

        if (brokerFees.length > 0) {
          updateFeeConfiguration(brokerFees);
        }
        // If no fees found, user will need to configure them manually
      }

      setLoadingState(FeeConfigurationLoadingState.READY);
      hasInitializedRef.current = true;

    } catch (error) {
      console.error('Error initializing fee configuration:', error);
      setLoadingState(FeeConfigurationLoadingState.ERROR);
    }
  }, [draft, draftLoading, updateFeeConfiguration]);

  // Helper function to get fee assignment title based on node type
  const getAssignFeesTitle = (nodeId: string): string => {
    const node = hierarchyOperations.findNode(nodeId);
    if (!node) return 'Assign Fees';

    switch (node.type) {
      case 'asset-class':
        return `Assign Asset Class Fees - ${formatAssetClassTypeDisplay(node.name as AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP)}`;
      case 'market-segment':
        return `Assign Market Segment Fees - ${formatMarketSegmentDisplay(node.name as MarketSegment, MARKET_SEGMENT_DISPLAY_MAP)}`;
      case 'sub-asset-class':
        return `Assign Sub-Asset Class Fees - ${formatSubAssetClassDisplay(node.name as SubAssetClass, SUB_ASSET_CLASS_DISPLAY_MAP)}`;
      default:
        return `Assign Fees - ${node.name}`;
    }
  };

  // Modal handlers
  const handleAddAssetClass = () => {
    const modalContext: ModalContext = {
      title: 'Add Asset Class',
      hierarchyLevel: 'asset-class'
    };
    openModal(ModalType.ASSET_CLASS_SELECTOR, modalContext);
  };

  const handleAddInstruments = () => {
    const modalContext: ModalContext = {
      title: 'Add Instruments',
      subtitle: 'Select instruments to add to your fee configuration'
    };
    openModal(ModalType.INSTRUMENT_SELECTION, modalContext);
  };

  const handleAssignFees = (nodeId: string) => {
    const node = hierarchyOperations.findNode(nodeId);
    if (!node) return;

    const modalContext: ModalContext = {
      title: getAssignFeesTitle(nodeId),
      subtitle: `Configure commission, overnight financing, and currency conversion fees for ${node.name}`,
      nodeId: nodeId,
      assignmentType: 'hierarchy',
      hierarchyLevel: node.type
    };
    openModal(ModalType.FEE_ASSIGNMENT, modalContext);
  };

  const handleEditFees = (nodeId: string) => {
    const node = hierarchyOperations.findNode(nodeId);
    if (!node) return;

    const modalContext: ModalContext = {
      title: `Edit Fees - ${node.name}`,
      subtitle: `Modify existing fee configuration for ${node.name}`,
      nodeId: nodeId,
      assignmentType: 'hierarchy',
      hierarchyLevel: node.type,
      ...(node.fees && { existingFees: node.fees })
    };
    openModal(ModalType.FEE_ASSIGNMENT, modalContext);
  };

  const handleRemoveFees = (nodeId: string) => {
    hierarchyOperations.removeFees(nodeId);
  };

  const handleRemoveLevel = (nodeId: string) => {
    hierarchyOperations.removeLevel(nodeId);
  };

  const handleAddSubLevel = (parentNodeId: string) => {
    const parentNode = hierarchyOperations.findNode(parentNodeId);
    if (!parentNode) return;

    let hierarchyLevel: 'asset-class' | 'market-segment' | 'sub-asset-class';
    let modalTitle: string;

    if (parentNode.type === 'asset-class') {
      hierarchyLevel = 'market-segment';
      modalTitle = 'Add Market Segment';
    } else if (parentNode.type === 'market-segment') {
      hierarchyLevel = 'sub-asset-class';
      modalTitle = 'Add Sub-Asset Class';
    } else {
      console.warn('Cannot add sub-level to this depth');
      return;
    }

    const modalContext: ModalContext = {
      title: modalTitle,
      hierarchyLevel,
      parentNodeId: parentNodeId,
      parentAssetClass: parentNode.rawValues.assetClass,
      ...(parentNode.rawValues.marketSegment && { parentMarketSegment: parentNode.rawValues.marketSegment })
    };
    openModal(ModalType.ASSET_CLASS_SELECTOR, modalContext);
  };

  const handleAddInstrument = (nodeId: string) => {
    const node = hierarchyOperations.findNode(nodeId);
    if (!node) return;

    console.log(`Adding instruments to node: ${node.name}`);

    const modalContext: ModalContext = {
      title: 'Add Instruments',
      subtitle: `Select instruments to add to ${node.name}`,
      nodeId: nodeId,
      parentAssetClass: node.rawValues.assetClass,
      ...(node.rawValues.marketSegment && { parentMarketSegment: node.rawValues.marketSegment }),
      ...(node.rawValues.subAssetClass && { parentSubAssetClass: node.rawValues.subAssetClass })
    };

    openModal(ModalType.INSTRUMENT_SELECTION, modalContext);
  };

  const handleEditInstrumentFees = (instrumentId: string) => {
    const instrumentInfo = hierarchyOperations.findInstrument(instrumentId);
    if (!instrumentInfo) return;

    const { instrument } = instrumentInfo;

    const modalContext: ModalContext = {
      title: `Edit Fees - ${instrument.symbol}`,
      subtitle: `Modify fee configuration for ${instrument.symbol} instrument`,
      instrumentId: instrumentId,
      assignmentType: 'instrument',
      ...(instrument.fees && { existingFees: instrument.fees })
    };
    openModal(ModalType.FEE_ASSIGNMENT, modalContext);
  };

  const handleRemoveInstrument = (instrumentId: string) => {
    hierarchyOperations.removeInstrument(instrumentId);
  };

  // Modal callback handlers

  // Hierarchy fee assignment handler
  const handleHierarchyFeeAssignment = useCallback((feeData: FeeFormData) => {
    if (!context?.nodeId) {
      console.error('No node ID context for hierarchy fee assignment');
      return;
    }

    const node = hierarchyOperations.findNode(context.nodeId);
    if (!node) {
      console.error('Could not find node:', context.nodeId);
      return;
    }

    console.log('Assigning fees to hierarchy node:', node.name);

    // Build the assignment context from the node
    const assignmentContext: FeeAssignmentContext = {
      assetClass: node.rawValues.assetClass as AssetClassType,
      name: `${node.name} fees`
    };

    if (node.rawValues.marketSegment) {
      assignmentContext.marketSegment = node.rawValues.marketSegment as MarketSegment;
    }

    if (node.rawValues.subAssetClass) {
      assignmentContext.subAssetClass = node.rawValues.subAssetClass as SubAssetClass;
    }

    // Transform form data to BrokerFeeConfiguration
    const brokerFeeConfig = transformFormToBrokerFeeConfig(feeData, assignmentContext);

    // Persist using hierarchy operations
    hierarchyOperations.assignFees(context.nodeId, brokerFeeConfig);
  }, [context, hierarchyOperations]);

  // Instrument fee assignment handler
  const handleInstrumentFeeAssignment = useCallback((feeData: FeeFormData) => {
    if (!context?.instrumentId) {
      console.error('No instrument ID for instrument fee assignment');
      return;
    }

    console.log('Assigning fees to instrument:', context.instrumentId);

    // Find the instrument in the hierarchy to get its taxonomy
    const instrumentInfo = hierarchyOperations.findInstrument(context.instrumentId);

    if (!instrumentInfo) {
      console.error('Could not find instrument in hierarchy:', context.instrumentId);
      return;
    }

    const { instrument } = instrumentInfo;

    // Build the assignment context for the instrument
    const assignmentContext: FeeAssignmentContext = {
      assetClass: instrument.taxonomy.assetClass as AssetClassType,
      instrumentId: context.instrumentId,
      name: `${instrument.symbol} fees`
    };

    if (instrument.taxonomy.marketSegment) {
      assignmentContext.marketSegment = instrument.taxonomy.marketSegment as MarketSegment;
    }

    if (instrument.taxonomy.subAssetClass) {
      assignmentContext.subAssetClass = instrument.taxonomy.subAssetClass as SubAssetClass;
    }

    // Transform form data to BrokerFeeConfiguration
    const brokerFeeConfig = transformFormToBrokerFeeConfig(feeData, assignmentContext);

    // Persist using instrument-specific operations
    hierarchyOperations.assignInstrumentFees(context.instrumentId, brokerFeeConfig);
  }, [context, hierarchyOperations]);

  // Smart fee assignment save handler with type discrimination
  const handleFeeAssignmentSave = useCallback((feeData: FeeFormData) => {
    if (!context?.assignmentType) {
      console.error('No assignment type specified in modal context');
      return;
    }

    console.log('Fee assignment save:', {
      assignmentType: context.assignmentType,
      level: context.level,
      instrumentId: context.instrumentId
    });

    // Route to appropriate handler based on assignment type
    if (context.assignmentType === 'instrument') {
      handleInstrumentFeeAssignment(feeData);
    } else if (context.assignmentType === 'hierarchy') {
      handleHierarchyFeeAssignment(feeData);
    } else {
      console.error('Unknown assignment type:', context.assignmentType);
    }
  }, [context, handleInstrumentFeeAssignment, handleHierarchyFeeAssignment]);

  const handleAssetClassAdd = useCallback((selection: { level: string; value: string; customValue: string; label: string; }) => {
    // Create hierarchy levels without fees - user will assign fees separately
    switch (selection.level) {
      case 'asset-class':
        hierarchyOperations.addAssetClass(selection.value, selection.value === 'other' ? selection.customValue : undefined);
        break;
      case 'market-segment':
        if (context?.parentNodeId) {
          hierarchyOperations.addMarketSegment(context.parentNodeId, selection.value, selection.value === 'other' ? selection.customValue : undefined);
        }
        break;
      case 'sub-asset-class':
        if (context?.parentNodeId) {
          hierarchyOperations.addSubAssetClass(context.parentNodeId, selection.value, selection.value === 'other' ? selection.customValue : undefined);
        }
        break;
    }
  }, [context, hierarchyOperations]);

  // Get all existing instruments from the current hierarchy
  const existingInstruments = useMemo(() => {
    const instruments: string[] = [];

    const extractInstruments = (nodes: typeof hierarchy) => {
      nodes.forEach(node => {
        // Add instruments from this node
        node.instruments.forEach(instrument => {
          instruments.push(instrument.symbol);
        });

        // Recursively extract from children
        extractInstruments(node.children);
      });
    };

    extractInstruments(hierarchy);
    return instruments;
  }, [hierarchy]);

  // Get all existing hierarchy levels from the current tree
  const existingHierarchyLevels = useMemo(() => {
    const assetClasses = new Set<string>();
    const marketSegments = new Map<string, Set<string>>(); // assetClass -> marketSegments
    const subAssetClasses = new Map<string, Set<string>>(); // "assetClass-marketSegment" -> subAssetClasses

    const extractLevels = (nodes: typeof hierarchy) => {
      nodes.forEach(node => {
        // Extract asset class
        if (node.type === 'asset-class') {
          assetClasses.add(node.rawValues.assetClass);
        }

        // Extract market segment
        if (node.type === 'market-segment' && node.rawValues.assetClass && node.rawValues.marketSegment) {
          const assetClass = node.rawValues.assetClass;
          if (!marketSegments.has(assetClass)) {
            marketSegments.set(assetClass, new Set());
          }
          const assetClassSet = marketSegments.get(assetClass);
          if (assetClassSet) {
            assetClassSet.add(node.rawValues.marketSegment);
          }
        }

        // Extract sub-asset class
        if (node.type === 'sub-asset-class' && node.rawValues.assetClass && node.rawValues.marketSegment && node.rawValues.subAssetClass) {
          const key = `${node.rawValues.assetClass}-${node.rawValues.marketSegment}`;
          if (!subAssetClasses.has(key)) {
            subAssetClasses.set(key, new Set());
          }
          const subAssetClassSet = subAssetClasses.get(key);
          if (subAssetClassSet) {
            subAssetClassSet.add(node.rawValues.subAssetClass);
          }
        }

        // Recursively extract from children
        extractLevels(node.children);
      });
    };

    extractLevels(hierarchy);
    return {
      assetClasses: Array.from(assetClasses),
      marketSegments: Object.fromEntries(
        Array.from(marketSegments.entries()).map(([key, value]) => [key, Array.from(value)])
      ),
      subAssetClasses: Object.fromEntries(
        Array.from(subAssetClasses.entries()).map(([key, value]) => [key, Array.from(value)])
      )
    };
  }, [hierarchy]);

  const handleInstrumentImport = useCallback((instruments: InstrumentSelection[]) => {
    console.log('Importing instruments to hierarchy', instruments);

    // Check if we're adding to a specific level (contextual) or doing bulk import
    if (context?.nodeId) {
      // Contextual placement: add instruments to specific node
      const node = hierarchyOperations.findNode(context.nodeId);
      if (node) {
        console.log(`Adding ${instruments.length} instruments to specific node: ${node.name}`);

        // Use instrument's actual taxonomy - build hierarchy as needed
        const instrumentsWithTaxonomy = instruments.map(instrument => ({
          symbol: instrument.symbol,
          assetClass: instrument.assetClass,
          ...(instrument.marketSegment && { marketSegment: instrument.marketSegment }),
          ...(instrument.subAssetClass && { subAssetClass: instrument.subAssetClass }),
          catalogId: instrument.catalogId
        }));

        hierarchyOperations.buildFromInstruments(instrumentsWithTaxonomy);
      }
    } else {
      // Bulk import: use the buildFromInstruments method with original metadata
      hierarchyOperations.buildFromInstruments(instruments as Array<{ symbol: string; assetClass: string; marketSegment?: string; subAssetClass?: string; catalogId: string; }>);
    }
  }, [context, hierarchyOperations]);

  // Hierarchy tree actions
  const treeActions = {
    onAssignFees: handleAssignFees,
    onEditFees: handleEditFees,
    onRemoveFees: handleRemoveFees,
    onRemoveLevel: handleRemoveLevel,
    onAddSubLevel: handleAddSubLevel,
    onAddInstrument: handleAddInstrument,
    onEditInstrumentFees: handleEditInstrumentFees,
    onRemoveInstrument: handleRemoveInstrument,
    onAddAssetClass: handleAddAssetClass,
    onAddInstruments: handleAddInstruments
  };

  // Loading skeleton component
  const LoadingSkeleton: React.FC = () => (
    <Card className="mx-6 mb-4" radius="lg" shadow="sm">
      <CardBody className="p-6">
        <Card className="mb-4" radius="sm" shadow="none">
          <CardBody className="p-4">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
              <span className="text-sm text-text-secondary">
                {loadingState === FeeConfigurationLoadingState.LOADING_CATALOG
                  ? 'Loading broker fee configuration...'
                  : 'Initializing fee configuration...'}
              </span>
            </div>
          </CardBody>
        </Card>

        <Card radius="sm" shadow="none" className="space-y-4">
          <CardBody className="p-4">
            {/* Skeleton for fee hierarchy - first level */}
            <Skeleton className="h-16 w-full rounded-lg" />
            <Card className="ml-4" radius="sm" shadow="none">
              <CardBody className="p-3 space-y-3">
                <Skeleton className="h-12 w-full rounded-md" />
                <Skeleton className="h-12 w-4/5 rounded-md" />
              </CardBody>
            </Card>
          </CardBody>
        </Card>

        <Card radius="sm" shadow="none" className="mt-4">
          <CardBody className="p-4">
            {/* Skeleton for fee hierarchy - second level */}
            <Skeleton className="h-16 w-full rounded-lg" />
            <Card className="ml-4" radius="sm" shadow="none">
              <CardBody className="p-3 space-y-3">
                <Skeleton className="h-12 w-full rounded-md" />
                <Skeleton className="h-12 w-3/5 rounded-md" />
              </CardBody>
            </Card>
          </CardBody>
        </Card>
      </CardBody>
    </Card>
  );

  const isLoading = loadingState === FeeConfigurationLoadingState.INITIALIZING ||
                    loadingState === FeeConfigurationLoadingState.LOADING_CATALOG;

  return (
    <div className={`fee-configuration-container h-[calc(100dvh-13rem)] max-h-[calc(100dvh-12rem)] flex flex-col ${className}`}>
      {/* Validation errors display */}
      {hierarchyErrors.length > 0 && loadingState === FeeConfigurationLoadingState.READY && (
        <div className="mx-6 mt-6 mb-6 flex-shrink-0">
          <Alert
            color="warning"
            variant="bordered"
            title="Configuration Issues"
            description={
              <ul className="space-y-1 mt-2">
                {hierarchyErrors.map((error, index) => (
                  <li key={index} className="text-sm">
                    • {error}
                  </li>
                ))}
              </ul>
            }
          />
        </div>
      )}

      {/* Main fee configuration card - Full screen with fixed header */}
      <Card
        radius="sm"
        shadow="sm"
        className="flex flex-col flex-1 min-h-0 w-full h-full"
      >
        <CardHeader className="flex flex-row items-center justify-center md:justify-between flex-shrink-0 border-b border-default-200">
          <div className="hidden md:block">
            <h3 className="text-lg font-semibold text-text-primary">Fee Configuration</h3>
            <p className="text-sm text-text-secondary">Configure fees by asset class hierarchy or individual instruments</p>
          </div>
          <div className="flex gap-2">
            <Button
              color="primary"
              startContent={<Plus className="w-4 h-4" />}
              onPress={treeActions.onAddAssetClass}
              isDisabled={isLoading}
            >
              <span className="hidden sm:inline">Add </span>Asset Class
            </Button>
            <Button
              variant="bordered"
              startContent={<Plus className="w-4 h-4" />}
              onPress={treeActions.onAddInstruments}
              isDisabled={isLoading}
            >
              <span className="hidden sm:inline">Add </span>Instruments
            </Button>
          </div>
        </CardHeader>

        <CardBody className="flex-1 overflow-y-auto p-0 min-h-0">
          {/* Main content */}
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <div className="h-full overflow-y-auto px-2 py-4">
              <FeeHierarchyTree
                hierarchy={hierarchy}
                actions={treeActions}
              />
            </div>
          )}
        </CardBody>
      </Card>

      {/* Modal management */}
      <FeeModalManager
        context={context}
        activeModal={activeModal}
        onClose={closeModal}
        onFeeAssignmentSave={handleFeeAssignmentSave}
        onAssetClassAdd={handleAssetClassAdd}
        onInstrumentImport={handleInstrumentImport}
        existingInstruments={existingInstruments}
        existingHierarchyLevels={existingHierarchyLevels}
      />
    </div>
  );
};